/**
 * Campaigns Context
 *
 * Global state management for campaigns module.
 * Provides campaign data, loading states, and CRUD operations.
 */

import { Campaign, CampaignALO, CampaignTarget, StoryBeat } from '@/types/campaigns'
import { createContext, FC, ReactNode, useCallback, useContext, useState } from 'react'

/* eslint-disable react-refresh/only-export-components -- context module */

interface CampaignsContextType {
    // Campaigns
    campaigns: Campaign[]
    currentCampaign: Campaign | null
    campaignsLoading: boolean
    setCampaigns: (campaigns: Campaign[]) => void
    setCurrentCampaign: (campaign: Campaign | null) => void
    addCampaign: (campaign: Campaign) => void
    updateCampaign: (id: string, updates: Partial<Campaign>) => void
    removeCampaign: (id: string) => void

    // Targets
    targets: CampaignTarget[]
    targetsLoading: boolean
    setTargets: (targets: CampaignTarget[]) => void
    addTarget: (target: CampaignTarget) => void
    updateTarget: (id: string, updates: Partial<CampaignTarget>) => void
    removeTarget: (id: string) => void

    // ALDs
    alds: CampaignALO[]
    aldsLoading: boolean
    setALDs: (alds: CampaignALO[]) => void
    addALD: (ald: CampaignALO) => void
    updateALD: (id: string, updates: Partial<CampaignALO>) => void
    removeALD: (id: string) => void

    // Story Beats
    beats: StoryBeat[]
    beatsLoading: boolean
    setBeats: (beats: StoryBeat[]) => void
    addBeat: (beat: StoryBeat) => void
    updateBeat: (id: string, updates: Partial<StoryBeat>) => void
    removeBeat: (id: string) => void

    // Loading states
    setLoadingState: (key: string, loading: boolean) => void
}

const CampaignsContext = createContext<CampaignsContextType | undefined>(undefined)

interface CampaignsProviderProps {
    children: ReactNode
}

export const CampaignsProvider: FC<CampaignsProviderProps> = ({ children }) => {
    const [campaigns, setCampaigns] = useState<Campaign[]>([])
    const [currentCampaign, setCurrentCampaign] = useState<Campaign | null>(null)
    const [targets, setTargets] = useState<CampaignTarget[]>([])
    const [alds, setALDs] = useState<CampaignALO[]>([])
    const [beats, setBeats] = useState<StoryBeat[]>([])

    const [campaignsLoading, setCampaignsLoading] = useState(false)
    const [targetsLoading, setTargetsLoading] = useState(false)
    const [aldsLoading, setALDsLoading] = useState(false)
    const [beatsLoading, setBeatsLoading] = useState(false)

    // Campaign operations
    const addCampaign = useCallback((campaign: Campaign) => {
        setCampaigns(prev => [campaign, ...(Array.isArray(prev) ? prev : [])])
    }, [])

    const updateCampaign = useCallback(
        (id: string, updates: Partial<Campaign>) => {
            setCampaigns(prev => (Array.isArray(prev) ? prev.map(c => (c._id === id ? { ...c, ...updates } : c)) : []))
            if (currentCampaign?._id === id) {
                setCurrentCampaign(prev => (prev ? { ...prev, ...updates } : null))
            }
        },
        [currentCampaign]
    )

    const removeCampaign = useCallback(
        (id: string) => {
            setCampaigns(prev => (Array.isArray(prev) ? prev.filter(c => c._id !== id) : []))
            if (currentCampaign?._id === id) {
                setCurrentCampaign(null)
            }
        },
        [currentCampaign]
    )

    // Target operations
    const addTarget = useCallback((target: CampaignTarget) => {
        setTargets(prev => [...prev, target])
    }, [])

    const updateTarget = useCallback((id: string, updates: Partial<CampaignTarget>) => {
        setTargets(prev => prev.map(t => (t._id === id ? { ...t, ...updates } : t)))
    }, [])

    const removeTarget = useCallback((id: string) => {
        setTargets(prev => prev.filter(t => t._id !== id))
    }, [])

    // ALD operations
    const addALD = useCallback((ald: CampaignALO) => {
        setALDs(prev => [...prev, ald])
    }, [])

    const updateALD = useCallback((id: string, updates: Partial<CampaignALO>) => {
        setALDs(prev => prev.map(a => (a._id === id ? { ...a, ...updates } : a)))
    }, [])

    const removeALD = useCallback((id: string) => {
        setALDs(prev => prev.filter(a => a._id !== id))
    }, [])

    // Beat operations
    const addBeat = useCallback((beat: StoryBeat) => {
        setBeats(prev => [...prev, beat])
    }, [])

    const updateBeat = useCallback((id: string, updates: Partial<StoryBeat>) => {
        setBeats(prev => prev.map(b => (b._id === id ? { ...b, ...updates } : b)))
    }, [])

    const removeBeat = useCallback((id: string) => {
        setBeats(prev => prev.filter(b => b._id !== id))
    }, [])

    // Loading state management
    const setLoadingState = useCallback((key: string, loading: boolean) => {
        switch (key) {
            case 'campaigns':
                setCampaignsLoading(loading)
                break
            case 'targets':
                setTargetsLoading(loading)
                break
            case 'alds':
                setALDsLoading(loading)
                break
            case 'beats':
                setBeatsLoading(loading)
                break
        }
    }, [])

    const value = {
        campaigns,
        currentCampaign,
        campaignsLoading,
        setCampaigns,
        setCurrentCampaign,
        addCampaign,
        updateCampaign,
        removeCampaign,
        targets,
        targetsLoading,
        setTargets,
        addTarget,
        updateTarget,
        removeTarget,
        alds,
        aldsLoading,
        setALDs,
        addALD,
        updateALD,
        removeALD,
        beats,
        beatsLoading,
        setBeats,
        addBeat,
        updateBeat,
        removeBeat,
        setLoadingState,
    }

    return <CampaignsContext.Provider value={value}>{children}</CampaignsContext.Provider>
}

export const useCampaigns = () => {
    const context = useContext(CampaignsContext)
    if (context === undefined) {
        throw new Error('useCampaigns must be used within a CampaignsProvider')
    }
    return context
}
